import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { TRPCClientError } from '@trpc/client'
import { useSidebarStore } from '@/stores/sidebarStore'
import { EllipsisVertical } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'
import { type RouterOutput, trpc } from '@/config/trpc'
import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { useBrand } from '@/context/brandContext'
import {
  CheckMarkIcon,
  Delete1Icon,
  DeleteIcon,
  EditIcon,
  PlusIcon,
  ShadowIcon,
} from '@/components/ui/icons/icons-new'

export type Workspace = RouterOutput['workspace']['list'][0]

export function SidebarNavigationListWorkspace() {
  const { toggleSidebar } = useSidebarStore()
  const navigate = useNavigate()
  const { isSwitching } = useBrand()

  const { data: workspaces, isLoading } = trpc.workspace.list.useQuery(undefined, {
    select: (data) => data.filter((workspace) => workspace.status === 'success'),
  })

  const [renamingId, setRenamingId] = useState<number | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const utils = trpc.useUtils()

  const { mutateAsync: deleteWorkspace } = trpc.workspace.delete.useMutation()
  const { mutateAsync: duplicateWorkspace } = trpc.workspace.duplicate.useMutation()
  const { mutateAsync: updateWorkspace } = trpc.workspace.update.useMutation()

  const handleDuplicate = async (id: number) => {
    try {
      await duplicateWorkspace({ id })
      utils.workspace.list.invalidate()
    } catch (error) {
      console.error(error)
      toast.error('Failed to duplicate workspace')
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteWorkspace({ id })
      utils.workspace.list.invalidate()

      if (location.pathname.includes(`/workspace/${id}`)) {
        navigate('/')
      }
    } catch (error) {
      console.error(error)
      // Deletion is refused for workspaces with published/scheduled posts —
      // surface the server's explanation instead of failing silently.
      toast.error(error instanceof TRPCClientError ? error.message : 'Failed to delete workspace')
    }
  }

  const handleStartRename = (workspace: Pick<Workspace, 'id' | 'title'>) => {
    setRenamingId(workspace.id)
    setRenameValue(workspace.title)
  }

  const handleCancelRename = () => {
    setRenamingId(null)
    setRenameValue('')
  }

  const handleRename = async () => {
    if (renamingId && renameValue.trim()) {
      try {
        await updateWorkspace({
          id: renamingId,
          data: { title: renameValue.trim() },
        })
        utils.workspace.list.invalidate()
        setRenamingId(null)
        setRenameValue('')
      } catch (error) {
        console.error(error)
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRename()
    } else if (e.key === 'Escape') {
      handleCancelRename()
    }
  }

  useEffect(() => {
    if (renamingId && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [renamingId])

  if (isLoading || isSwitching) {
    return (
      <ul className="space-y-2">
        {[...Array(3)].map((_, index) => (
          <li key={index} className="animate-pulse" style={{ animationDelay: `${index * 0.2}s` }}>
            <div className="bg-hover flex w-[95%] items-center space-x-2 rounded-sm px-2 py-1.5">
              <div className="h-4 rounded-md" />
            </div>
          </li>
        ))}
      </ul>
    )
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <ul>
        <li className={`hover:bg-hover rounded-sm transition-colors`}>
          <Link
            to={`/`}
            onClick={toggleSidebar}
            className="flex w-full items-center justify-between px-2 py-1.5">
            <span className="max-w-full truncate">Create new workspace</span>
            <PlusIcon width={20} height={20} color="var(--foreground)" />
          </Link>
        </li>
      </ul>
    )
  }

  return (
    <div className={`max-h-[calc(100dvh-480px)] overflow-y-auto`}>
      {groupItemsByTime(workspaces).map(([group, itemsInGroup]) => (
        <div key={group} className="space-y-1">
          <span className="text-muted-foreground text-xs font-medium">{group}</span>
          <ul className="w-full space-y-1">
            {itemsInGroup.map((item) => (
              <li
                key={item.id}
                title={item.title}
                className={`flex w-full items-center rounded-sm transition-colors ${
                  location.pathname === `/workspace/${item.id}` ? 'bg-hover' : 'hover:bg-hover'
                }`}>
                {renamingId === item.id ? (
                  <div className="flex w-full items-center gap-2 px-2 py-1.5">
                    <input
                      ref={inputRef}
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="outline-hidden min-w-0 flex-1 border-none bg-transparent text-sm"
                      autoFocus
                    />
                    <Button variant="ghost" size="icon-sm" onClick={handleRename}>
                      <CheckMarkIcon width={20} height={20} color="var(--foreground)" />
                    </Button>
                    <Button variant="ghost" size="icon-sm" onClick={handleCancelRename}>
                      <DeleteIcon width={20} height={20} color="var(--foreground)" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Link
                      to={`/workspace/${item.id}`}
                      onClick={() => {
                        toggleSidebar()
                      }}
                      className="flex min-w-0 flex-1 items-center px-2 py-1.5 text-sm font-medium">
                      <span className="w-full overflow-hidden truncate whitespace-nowrap">
                        {item.title}
                      </span>
                    </Link>
                    <DropdownMenu>
                      <DropdownMenuTrigger className="outline-hidden cursor-pointer rounded-md border-none p-2 text-sm transition-colors">
                        <EllipsisVertical className="h-4 w-4" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        className="bg-background w-[150px] shadow-none"
                        side="right"
                        align="end"
                        sideOffset={10}
                        alignOffset={0}
                        avoidCollisions
                        style={{ caretColor: 'transparent' }}>
                        <DropdownMenuItem
                          className="flex items-center gap-2"
                          onClick={() => handleDuplicate(item.id)}>
                          <ShadowIcon width={20} height={20} color="var(--foreground)" />
                          Duplicate
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          className="flex items-center gap-2"
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            handleStartRename(item)
                          }}>
                          <EditIcon width={20} height={20} color="var(--foreground)" /> Rename
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          className="flex items-center gap-2"
                          onClick={() => handleDelete(item.id)}>
                          <Delete1Icon width={20} height={20} color="var(--foreground)" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

const groupItemsByTime = (
  items: Pick<Workspace, 'id' | 'title' | 'updatedAt'>[],
): [string, Pick<Workspace, 'id' | 'title' | 'updatedAt'>[]][] => {
  const today = new Date().setHours(0, 0, 0, 0)
  const lastWeek = new Date(today).setDate(new Date().getDate() - 7)

  interface GroupedItems {
    Today: Pick<Workspace, 'id' | 'title' | 'updatedAt'>[]
    'Last Week': Pick<Workspace, 'id' | 'title' | 'updatedAt'>[]
    Later: Pick<Workspace, 'id' | 'title' | 'updatedAt'>[]
  }

  const grouped: GroupedItems = {
    Today: [],
    'Last Week': [],
    Later: [],
  }

  items.forEach((item) => {
    const itemDate = new Date(item.updatedAt).setHours(0, 0, 0, 0)

    if (itemDate === today) {
      grouped['Today'].push(item)
    } else if (itemDate >= lastWeek && itemDate < today) {
      grouped['Last Week'].push(item)
    } else {
      grouped['Later'].push(item)
    }
  })

  return Object.entries(grouped).filter(([_, items]) => items.length > 0)
}
